import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import { toast, ToastContainer } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'react-toastify/dist/ReactToastify.css';
import '../dashboard/dashboard.css';
import './OdinWorkspaceTheme.css';
import trident from '../assets/odin-trident.png';
import axios from '../api/axiosInstance';
import Sidebar from '../Components/Sidebar';
import { getAuthToken, logout } from '../auth/cookieUtils';
import { buildActionItemPdfRows, formatActionItemDate } from '../utils/exportRows';

const ActionItems = () => {
  const navigate = useNavigate();
  const token = getAuthToken();

  const [areas, setAreas] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(true);
  const [sortBy, setSortBy] = useState('dueDate');
const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  useEffect(() => {
    if (!selectedLocationId) {
      setAreas([]);
      setSelectedAreas([]);
      return;
    }
    const fetchAreas = async () => {
      try {
        const areasResponse = await axios.get(`/api/areas/location/${selectedLocationId}`);
        setAreas(Array.isArray(areasResponse.data) ? areasResponse.data : []);
        setSelectedAreas([]); // reset areas when location changes
      } catch (error) {
        console.error('Error fetching areas:', error);
        if ([401, 403].includes(error.response?.status)) {
          logout();
        }
      }
    };
    fetchAreas();
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) {
      setActionItems([]);
      return undefined;
    }

    let ignoreResponse = false;

    const fetchActionItems = async () => {
      try {
        setLoading(true);
        let url = `/api/action-items?locationId=${selectedLocationId}`;
        selectedAreas.forEach(areaId => url += `&areaIds=${areaId}`);

        const response = await axios.get(url);
        if (!ignoreResponse) setActionItems(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (!ignoreResponse) console.error('Error fetching action items:', error);
      } finally {
        if (!ignoreResponse) setLoading(false);
      }
    };

    fetchActionItems();

    return () => {
      ignoreResponse = true;
    };
  }, [selectedLocationId, selectedAreas]);

  const handleLogout = () => {
    logout();
  };

  const toggleAreaSelection = (areaId) => {
    setSelectedAreas((prev) =>
      prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
    );
  };

  const markAsComplete = async (itemId) => {
    try {
      await axios.put(`/api/action-items/complete/${itemId}`);
      toast.success('Action item marked as complete! ✅');
      setActionItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId ? { ...item, completed: true, status: "Closed" } : item
        )
      );
    } catch (error) {
      console.error('Error marking action item complete:', error);
      toast.error('Failed to mark action item complete. ❌');
    }
  };

  const handleSort = (field) => {
  if (sortBy === field) {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  } else {
    setSortBy(field);
    setSortDirection('asc');
  }
};

  const exportToPDF = () => {
    if (sortedActionItems.length === 0) {
      toast.info('There are no action items to export for the selected filters.');
      return;
    }
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text("Action Items Report", 14, 20);

    const tableColumn = ["Equipment Name", "Action Text", "Category", "Type", "Priority", "Status", "Completed", "Date"];
    const tableRows = buildActionItemPdfRows(sortedActionItems);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 1.8, overflow: 'linebreak' },
    });

    doc.save("action_items_report.pdf");
  };

  const getSortableValue = (item, field) => {
  if (field === 'dueDate') {
    return item.dueDate ? new Date(item.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
  }

  if (field === 'completed') {
    return item.completed ? 1 : 0;
  }

  if (field === 'priority') {
    return item.priority ?? Number.MAX_SAFE_INTEGER;
  }

  return item[field] ?? '';
};

  const filteredActionItems = showOnlyIncomplete
  ? actionItems.filter((item) => !item.completed)
  : actionItems;

const sortedActionItems = useMemo(() => {
  return [...filteredActionItems].sort((a, b) => {
    const aValue = getSortableValue(a, sortBy);
    const bValue = getSortableValue(b, sortBy);

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return sortDirection === 'asc'
      ? String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      : String(bValue).localeCompare(String(aValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
  });
}, [filteredActionItems, sortBy, sortDirection]);

  return (
    <div className="dashboard-page odin-workspace-page action-items-workspace-page">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      {/* Top Navbar */}
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="ODIN Logo" className="navbar-logo" />
          {/* New Shared Company/Location Selector */}
          <CompanyLocationSelector
            onLocationChange={(locationId) => setSelectedLocationId(locationId)}
          />
        </div>
        <div className="topbar-center"><span className="page-title">Action Items</span></div>
        <div className="topbar-right"><button className="logout-button" onClick={handleLogout}>Logout</button></div>
      </div>

      <div className="dashboard-main">
        {/* Left Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <main className="dashboard-content workspace-content">
          <header className="workspace-page-heading">
            <span className="workspace-page-eyebrow">Maintenance follow-through</span>
            <h1>Action Items</h1>
            <p>Prioritize open work, review due dates, and close completed actions for the selected location.</p>
          </header>

          <section className="workspace-panel action-filter-toolbar">
            <div className="workspace-section-heading">
              <div>
                <h2>Area filter</h2>
                <p>{selectedAreas.length > 0 ? `${selectedAreas.length} selected` : 'Showing all areas'}</p>
              </div>
              <label className={`workspace-toggle${showOnlyIncomplete ? ' selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={showOnlyIncomplete}
                  onChange={() => setShowOnlyIncomplete(prev => !prev)}
                />
                <span>Show only incomplete</span>
              </label>
            </div>

            <div className="workspace-area-options">
                  {areas.map((area) => (
                    <button
                      type="button"
                      key={area.id}
                      className={`workspace-area-option${selectedAreas.includes(area.id) ? ' selected' : ''}`}
                      onClick={() => toggleAreaSelection(area.id)}
                    >
                      {area.name}
                    </button>
                  ))}
            </div>
          </section>

          <div className="workspace-results-toolbar">
            <div className="workspace-result-count">
              <span>Current results</span>
              <strong>{sortedActionItems.length.toLocaleString()} {sortedActionItems.length === 1 ? 'action item' : 'action items'}</strong>
            </div>
            <button type="button" className="export-btn" onClick={exportToPDF} disabled={!selectedLocationId || sortedActionItems.length === 0}>Export report</button>
          </div>

          <section className="equipment-table-section workspace-table-panel">
          
            {loading ? (
              <div className="workspace-empty-state">Loading action items...</div>
            ) : (
              <div className="table-scroll-container">
                 
                <table className="equipment-table">
                  <thead>
  <tr>
    <th onClick={() => handleSort('equipmentName')}>
      Equipment Name {sortBy === 'equipmentName' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('actionText')}>
      Action Text {sortBy === 'actionText' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('category')}>
      Category {sortBy === 'category' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('type')}>
      Type {sortBy === 'type' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('priority')}>
      Priority {sortBy === 'priority' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('status')}>
      Status {sortBy === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('completed')}>
      Completed {sortBy === 'completed' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
    <th onClick={() => handleSort('dueDate')}>
      Date {sortBy === 'dueDate' && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
  </tr>
</thead>
                  <tbody>
                    {filteredActionItems.length > 0 ? (
                      sortedActionItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.equipmentName || '-'}</td>
                            <td>{item.actionText || '-'}</td>
                            <td>{item.category || '-'}</td>
                            <td>{item.type || '-'}</td>
                            <td>{item.priority !== null ? item.priority : '-'}</td>
                            <td>{item.status || '-'}</td>
                            <td>
                              {item.completed ? (
                                "Yes"
                              ) : (
                                <button type="button" onClick={() => markAsComplete(item.id)} className="complete-button">
                                  Mark Complete
                                </button>
                              )}
                            </td>
                            <td>{formatActionItemDate(item.dueDate)}</td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td className="workspace-table-empty" colSpan="8">
                          No action items available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default ActionItems;
